import { Component, createRef } from 'react'
import { connect } from 'react-redux'
import { useFlag } from '@calendly/feature-check-react'
import { ScheduleSubmitButton } from './schedule_submit_button'
import { parseQuery } from '@calendly/http-tools'
import { nanoid } from 'nanoid'
import { uniqBy } from 'lodash-es'
import styled from 'astroturf/react'
import { Segment } from '@calendly/segment'

import { withI18n } from 'locales'
import { isMultipleLocation, isWithoutLocation } from 'services/location'
import InviteeInputs from 'components/invitee_inputs'
import MultipleLocation from 'components/location/multiple'
import SingleLocation from 'components/location/single'
import CustomFields from 'components/custom_fields'
import TextReminder from 'components/text_reminder'
import {
  isPollSelector,
  locationConfigurationsSelector,
  ignoreLocationConfigurationsSelector,
  showMailchimpInviteeConsentSelector
} from 'selectors/event_type'
import { eventTypeSelector } from 'root-api'
import { isPaymentCheckoutAvailable } from 'selectors/payment'
import {
  inviteeGuestsErrorsSelector,
  inviteeInvalidGuestEmailsSelector,
  singleLocationConfigurationSelector
} from 'selectors'
import { isGdprAppliedSelector } from 'selectors/gdpr'
import { embedSelector, showRecaptchaChallengeSelector } from 'selectors/scheduling'
import { buildRequestId } from 'services/request_id'
import { buildNewInvitee } from 'services/invitee'
import { buildAnswers } from 'services/answers'
import { buildGuests } from 'services/guests'
import { subscribeEmbedPrefill } from 'services/embed_prefill_handoff'
import { maybeRetrieveData } from 'services/autofill'
import {
  formFieldUpdated,
  formUnmounted,
  selectLocationConfiguration,
  submitPollVotes,
  submitScheduling,
  dismissRecaptchaChallenge,
} from 'actions/scheduling'
import { dismissScheduleError, dismissValidationError } from 'actions/errors'
import { currentInviteeSelector } from 'selectors/current_invitee'

import Payment from '../payment'
import { ExternalCommunicationConsent } from '../fields/external_communication_consent'
import IntercomConfirmation from '../intercom_confirmation'
import GuestsField from '../fields/guest'
import BookingField from '../booking_field'
import RecaptchaPopup from '../recaptcha_popup'
import { EnterDetailsFieldStack } from '../form_field_spacing'
import _Container from "scheduling_form-Container.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/enter_details/booking_form/scheduling_form.js?styleId=Container";


const Container = /*#__PURE__*/styled(EnterDetailsFieldStack, null, {
  displayName: "Container",
  styles: _Container
})

class SchedulingForm extends Component {
  constructor(props) {
    super(props)
    const { client, platform, booking_flow: bookingFlow, creator_uuid: creatorUuid } = parseQuery(window.location.search)
    this.state = {
      event: this.buildEventState(),
      invitee: this.buildInviteeState(),
      event_fields: this.buildEventFieldsState(),
      inviteeGuests: this.buildInviteeGuests(),
      client: client ? { client, platform } : false,
      bookingFlow,
      creatorUuid,
    }
    this.scheduleSubmitButtonRef = createRef()
  }

  componentDidMount() {
    this.props.dismissError()
    this.unsubscribeEmbedPrefill = subscribeEmbedPrefill((p) => this.applyEmbedPrefill(p))
  }

  componentDidUpdate() {
    switch (this.props.event_type.invitee_name_format) {
      case 'separated':
        this.updateFromAutoFillSeparate()
        break
      case 'combined':
        this.updateFromAutoFillCombined()
        break
      default:
        break
    }
  }

  updateFromAutoFillCombined() {
    if (!this.state.invitee.email.value && !this.state.invitee.full_name.value && Object.keys(this.props.autofillData).length !== 0) {
      this.setState((state) => ({
        invitee: {
          ...state.invitee,
          email: {
            ...state.invitee.email,
            value: this.props.autofillData.email
          },
          full_name: {
            ...state.invitee.full_name,
            value: this.props.autofillData.full_name
          }
        }
      }))
    }
  }

  updateFromAutoFillSeparate() {
    if (
      !this.state.invitee.email.value
      && !this.state.invitee.first_name.value
      && !this.state.invitee.last_name.value
      && Object.keys(this.props.autofillData
      ).length !== 0) {
      this.setState((state) => ({
        invitee: {
          ...state.invitee,
          email: {
            ...state.invitee.email,
            value: this.props.autofillData.email
          },
          first_name: {
            ...state.invitee.first_name,
            value: this.props.autofillData.first_name
          },
          last_name: {
            ...state.invitee.last_name,
            value: this.props.autofillData.last_name
          }
        }
      }))
    }
  }

  componentWillUnmount() {
    this.unsubscribeEmbedPrefill?.()
    this.props.onFieldUnmount()
  }

  applyEmbedPrefill(p) {
    if (!p) return

    const { invitee_name_format: nameFormat } = this.props.event_type

    if (p.email) this.props.onFieldUpdated('email', p.email)
    if (nameFormat === 'separated') {
      if (p.first_name) this.props.onFieldUpdated('first_name', p.first_name)
      if (p.last_name) this.props.onFieldUpdated('last_name', p.last_name)
    } else if (nameFormat === 'combined' && p.full_name) {
      this.props.onFieldUpdated('full_name', p.full_name)
    }

    const customAnswerUpdates = []
    Object.entries(p.customAnswers).forEach(([key, value]) => {
      const m = key.match(/^a(\d+)$/)
      if (!m || value == null) return
      const index = this.props.customFields.findIndex((f) => f.position + 1 === parseInt(m[1], 10))
      if (index < 0) return
      customAnswerUpdates.push({ index, value })
      this.props.onFieldUpdated(`question_${index}`, value)
    })

    this.setState((state) => {
      const nextInvitee = { ...state.invitee }
      if (p.email) {
        nextInvitee.email = { ...nextInvitee.email, value: p.email }
      }
      if (nameFormat === 'separated') {
        if (p.first_name) {
          nextInvitee.first_name = { ...nextInvitee.first_name, value: p.first_name }
        }
        if (p.last_name) {
          nextInvitee.last_name = { ...nextInvitee.last_name, value: p.last_name }
        }
      } else if (nameFormat === 'combined' && p.full_name) {
        nextInvitee.full_name = { ...nextInvitee.full_name, value: p.full_name }
      }

      let nextEventFields = state.event_fields
      if (customAnswerUpdates.length > 0) {
        nextEventFields = nextEventFields.slice()
        customAnswerUpdates.forEach(({ index, value }) => {
          nextEventFields[index] = { ...nextEventFields[index], value }
        })
      }

      const nextGuests = (this.isEventGuestsEnabled() && p.guests?.length > 0)
        ? p.guests.map((email) => this.buildInviteeGuest(email))
        : state.inviteeGuests

      return {
        invitee: nextInvitee,
        event_fields: nextEventFields,
        inviteeGuests: nextGuests,
      }
    }, this.onGuestsChange)
  }

  onInviteeFieldChange = (event) => {
    this.props.onFieldUpdated(event.target.name, event.target.value)
    this.setModelState('invitee', event.target.name,
      { value: event.target.value })
  }

  onCustomFieldChange = (index, props) => {
    this.props.onFieldUpdated(`question_${index}`, props.value)
    this.setCustomFieldState(index, props)
  }

  onTextReminderChange = (props) => {
    this.props.onFieldUpdated('phone_number', props.value)
    this.setModelState('invitee', 'phone_number', props)
  }

  onGuestsAdd = (email) => {
    this.setState((state) => ({
      inviteeGuests: [].concat(state.inviteeGuests, this.buildInviteeGuest(email))
    }), this.onGuestsChange)
  }

  onGuestsRemove = (guestId) => {
    this.setState(
      (prevState) => ({
        inviteeGuests: prevState.inviteeGuests.filter(({ id }) => id !== guestId),
      }), this.onGuestsChange,
    )
  }

  onGuestsUpdate = (guestId, newEmail) => {
    if (!newEmail) {
      this.onGuestsRemove(guestId)
      return
    }

    this.setState((prevState) => ({
      inviteeGuests: prevState.inviteeGuests.map(({ id, email }) => ({
        id,
        email: id === guestId ? newEmail : email,
      })),
    }), this.onGuestsChange)
  }

  onGuestsChange = () => {
    const emails = this.state.inviteeGuests.map(({ email }) => email)
    const uniqEmails = uniqBy(emails, (e) => e.toLocaleLowerCase())

    return this.setModelState('event', 'guest_emails', uniqEmails)
  }

  onExternalCommunicationConsentChange = (value) => {
    this.setModelState('invitee', 'external_communication_consent', value)
  }

  onSubmit = (event) => {
    event.preventDefault()

    const requestId = buildRequestId(
      this.props.requestKeySalt,
      this.props.spotStartTime,
      this.props.event_type.uuid,
      this.inviteeNameValue()
    )

    this.unfinishedRecaptchaModalAnalytic()

    return this.props.onSubmit(
      this.state,
      this.paymentApi,
      this.props.currentInvitee,
      requestId,
      this.state.client
    )
  }

  unfinishedRecaptchaModalAnalytic = () => {
    if (typeof this.state.recaptcha_token_version_2 === 'undefined' && this.props.showRecaptchaChallenge) {
      Segment.track('abuse_prevention.invitee.unfinished_recaptcha_continue', {
        publisher_id: this.props.profile.id
      })
    }
  }

  render() {
    return (
      <form
        autoComplete="off"
        autoCorrect="off"
        noValidate
        onSubmit={this.onSubmit}
      >
        <Container bookingAppRedesign={this.props.bookingAppRedesign} embedCustomColors={this.props.embedCustomColors}>
          {this.renderInviteeInputs()}
          {this.renderInviteeGuests()}
          {this.renderLocation()}
          {this.renderCustomFields()}
          {this.renderTextReminder()}
          {this.renderPaymentForm()}
          {this.renderIntercomConfirmation()}
          {this.renderExternalCommunicationConsent()}
          {this.renderRecaptchaChallengePopup()}
        </Container>
        <ScheduleSubmitButton
          ref={this.scheduleSubmitButtonRef}
          type="submit"
          size="large"
          loading={this.props.status === 'submitting'}
        >
          {this.props.isPoll
            ? this.props.i18n.t('booking.templates.poll.share_times_button')
            : this.props.i18n.t(this.props.bookingAppRedesign ? 'booking.templates.schedule_meeting' : 'booking.templates.schedule_event')}
        </ScheduleSubmitButton>
      </form>
    )
  }

  renderInviteeInputs() {
    return (
      <InviteeInputs
        fullName={this.state.invitee.full_name}
        firstName={this.state.invitee.first_name}
        lastName={this.state.invitee.last_name}
        email={this.state.invitee.email}
        onChange={this.onInviteeFieldChange}
      />
    )
  }

  renderInviteeGuests() {
    if (!this.isEventGuestsEnabled()) {
      return null
    }
    return (
      <GuestsField
        guests={this.state.inviteeGuests}
        onAdd={this.onGuestsAdd}
        onRemove={this.onGuestsRemove}
        onUpdate={this.onGuestsUpdate}
        errors={this.props.guestErrors}
        invalidEmails={this.props.invalidGuestEmails}
        dismissError={this.props.dismissGuestsErrors}
      />
    )
  }

  renderLocation() {
    if (isWithoutLocation(this.props.ignoreLocationConfigurations, this.props.locationConfigurations)) {
      return null
    }

    if (isMultipleLocation(this.props.locationConfigurations)) {
      return <MultipleLocation />
    }
    return <SingleLocation />
  }

  renderCustomFields() {
    if (this.props.isPoll) {
      return null
    }

    return (
      <CustomFields
        fields={this.state.event_fields}
        onChange={this.onCustomFieldChange}
      />
    )
  }

  renderTextReminder() {
    if (!this.props.event_type.sms_reminder_enabled) {
      return null
    }

    return (
      <TextReminder
        value={this.state.invitee.phone_number.value}
        required={this.state.invitee.phone_number.required}
        onChange={this.onTextReminderChange}
      />
    )
  }

  registerApi = (api) => {
    this.paymentApi = api
    return this.paymentApi
  }

  handlePaypalAuthorizedAutoSubmit = () => {
    this.scheduleSubmitButtonRef.current?.click()
  }

  renderPaymentForm() {
    if (!this.props.isActivePaymentMethod) {
      return null
    }
    return (
      <BookingField>
        <Payment
          registerApi={this.registerApi}
          onPaypalAuthorized={this.handlePaypalAuthorizedAutoSubmit}
        />
      </BookingField>
    )
  }

  renderIntercomConfirmation() {
    if (!this.props.isSubmittingIntercomSheet) {
      return null
    }
    return <IntercomConfirmation />
  }

  renderExternalCommunicationConsent() {
    if (!this.props.isGdprApplied || !this.props.showMailchimpInviteeConsent) {
      return null
    }
    return (
      <ExternalCommunicationConsent
        value={this.state.invitee.external_communication_consent}
        onChange={this.onExternalCommunicationConsentChange}
      />
    )
  }

  renderRecaptchaChallengePopup() {
    if (this.props.showRecaptchaChallenge) {
      return (
        <RecaptchaPopup
          onClose={() => {
            this.dismissRecaptchaPopup()
            this.closeRecaptchaModalAnalytic()
          }}
          onConfirm={this.onSubmit}
          setVerifyCallback={this.setRecaptchaV2VerifyCallback}
          loading={this.props.status === 'submitting'}
        />
      )
    }

    return null
  }

  buildInviteeAttribute(attribute, required) {
    const value = this.getInviteeValue(attribute)
    this.props.onFieldUpdated(attribute, value)

    return {
      name: attribute,
      required,
      value,
    }
  }

  buildInviteeGuest = (email) => ({
    id: nanoid(5),
    email,
  })

  buildInviteeGuests() {
    return buildGuests().map(this.buildInviteeGuest)
  }

  buildEventState() {
    return {
      location: '',
      guest_emails: buildGuests(),
    }
  }

  buildInviteeState() {
    const inviteeState = {}

    switch (this.props.event_type.invitee_name_format) {
      case 'separated':
        inviteeState.first_name = this.buildInviteeAttribute('first_name', true)
        inviteeState.last_name = this.buildInviteeAttribute('last_name', true)
        break
      case 'combined':
        inviteeState.full_name = this.buildInviteeAttribute('full_name', true)
        break
      default:
        break
    }

    inviteeState.email = this.buildInviteeAttribute('email', true)

    // It's important for us to differentiate true false and null values here.
    // null means consent checkbox was never shown for GDPR user
    if (this.props.isGdprApplied) {
      if (this.props.showMailchimpInviteeConsent) {
        inviteeState.external_communication_consent = false
      }
    } else if (this.props.showMailchimpInviteeConsent) {
      inviteeState.external_communication_consent = true
    }

    if (this.props.event_type.sms_reminder_enabled) {
      inviteeState.phone_number = this.buildInviteeAttribute('phone_number', false)
    }

    return inviteeState
  }

  buildEventFieldsState() {
    const { customFields } = this.props
    const predefinedAnswers = buildAnswers()
    return customFields.map((field) => ({
      ...field,
      value: predefinedAnswers[field.position + 1] || '',
    }))
  }

  inviteeNameValue() {
    if (this.props.event_type.invitee_name_format === 'separated') {
      return `${this.state.invitee.first_name.value}|${this.state.invitee.last_name.value}`
    }
    return this.state.invitee.full_name.value
  }


  setModelState(model, attribute, value) {
    const isTrueObject = (possibleObject) => (typeof possibleObject === 'object') && !Array.isArray(possibleObject)

    return this.setState((prevState) => {
      const newValue = isTrueObject(value) ? { ...prevState[model][attribute], ...value } : value
      return {
        [model]: {
          ...prevState[model],
          [attribute]: newValue,
        },
      }
    })
  }

  setCustomFieldState(index, props) {
    return this.setState((state) => {
      const newEventFields = state.event_fields.slice()
      newEventFields[index] = Object.assign({}, newEventFields[index], props)
      return { event_fields: newEventFields }
    })
  }

  setRecaptchaV2VerifyCallback = (token) => {
    this.setState({ recaptcha_token_version_2: token })
  }

  dismissRecaptchaPopup = () => {
    this.setState({ recaptcha_token_version_2: undefined })
    this.props.dismissRecaptchaChallenge()
  }

  closeRecaptchaModalAnalytic = () => {
    Segment.track('abuse_prevention.invitee.recaptcha_modal_closed', {
      publisher_id: this.props.profile.id
    })
  }

  getInviteeValue(attribute) {
    const { autofillData, currentInvitee } = this.props
    const newInvitee = buildNewInvitee()

    return newInvitee[attribute] || (currentInvitee || {})[attribute] || autofillData[attribute] || ''
  }

  isEventGuestsEnabled() {
    return this.props.event_type.guests_allowed
  }
}

const mapStateToProps = (state) => ({
  status: state.scheduling.status,
  embedCustomColors: embedSelector(state).color_customization,
  event_type: eventTypeSelector(state),
  customFields: eventTypeSelector(state).custom_fields,
  isActivePaymentMethod: isPaymentCheckoutAvailable(eventTypeSelector(state)),
  currentInvitee: currentInviteeSelector(state),
  isSubmittingIntercomSheet: state.scheduling.is_submitting_intercom,
  guestErrors: inviteeGuestsErrorsSelector(state),
  invalidGuestEmails: inviteeInvalidGuestEmailsSelector(state),
  locationConfigurations: locationConfigurationsSelector(state),
  singleLocationConfiguration: singleLocationConfigurationSelector(state),
  ignoreLocationConfigurations: ignoreLocationConfigurationsSelector(state),
  showMailchimpInviteeConsent: showMailchimpInviteeConsentSelector(state),
  isGdprApplied: isGdprAppliedSelector(state),
  isPoll: isPollSelector(state),
  showRecaptchaChallenge: showRecaptchaChallengeSelector(state),
  autofillData: maybeRetrieveData(state, eventTypeSelector(state)),
})


const mapDispatchToProps = (dispatch, ownProps) => ({
  onFieldUpdated(name, value) {
    return dispatch(formFieldUpdated(name, value))
  },

  onFieldUnmount() {
    return dispatch(formUnmounted())
  },

  onSubmit(formData, paymentApi, currentInvitee, requestId, client) {
    if (ownProps.isPoll) {
      return dispatch(submitPollVotes(ownProps.spotStartTimes, formData, requestId))
    }
    return dispatch(submitScheduling(ownProps.spotStartTime, formData, paymentApi, currentInvitee, requestId, client))
  },

  dismissError() {
    return dispatch(dismissScheduleError())
  },

  dismissValidationError(model, attribute) {
    return dispatch(dismissValidationError(model, attribute))
  },

  dismissGuestsErrors() {
    dispatch(dismissValidationError('event', 'event_guests'))
    return dispatch(dismissValidationError('event', 'event_guests_emails'))
  },

  dismissRecaptchaChallenge() {
    return dispatch(dismissRecaptchaChallenge())
  },

  onLocationSelected(locationConfiguration) {
    return dispatch(selectLocationConfiguration(locationConfiguration))
  },
})

const ConnectedSchedulingForm = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withI18n(SchedulingForm))

const SchedulingFormWithFieldSpacing = (props) => {
  const bookingAppRedesign = useFlag('booking_app_redesign', false)
  return <ConnectedSchedulingForm {...props} bookingAppRedesign={bookingAppRedesign} />
}

export default SchedulingFormWithFieldSpacing
